import { supabase } from '../lib/supabase';

export const supplierService = {
  // ============================================
  // SUPPLIER MANAGEMENT
  // ============================================

  // Get all suppliers with role filtering
  getAllSuppliers: async (filters = {}) => {
    try {
      let query = supabase?.from('user_profiles')?.select(`
        *,
        case_assignments:supplier_case_assignments(
          id,
          case_id,
          machine_id,
          status,
          assigned_at,
          notes
        ),
        machines:machines(
          id,
          machine_name,
          machine_code,
          status,
          location
        )
      `);

      // Filter only users with supplier roles by checking if they have machines or case assignments
      const { data, error } = await query?.order('created_at', { ascending: false });
      
      if (error) throw error;
      
      // Filter users who have machines (indicating they are suppliers)
      const suppliers = data?.filter(user => {
        return user?.machines && user?.machines?.length > 0;
      });

      // Apply additional filters
      let filteredSuppliers = suppliers;

      if (filters?.status && filters?.status !== 'all') {
        filteredSuppliers = filteredSuppliers?.filter(supplier => 
          supplier?.machines?.some(machine => 
            machine?.status === filters?.status
          )
        );
      }
      
      return filteredSuppliers?.map(supplier => ({
        id: supplier?.id,
        fullName: supplier?.full_name,
        email: supplier?.email,
        companyName: supplier?.company_name,
        phone: supplier?.phone,
        address: supplier?.address,
        caseAssignments: supplier?.case_assignments?.map(assignment => ({
          id: assignment?.id,
          caseId: assignment?.case_id,
          machineId: assignment?.machine_id,
          status: assignment?.status,
          assignedAt: assignment?.assigned_at,
          notes: assignment?.notes
        })),
        machines: supplier?.machines?.map(machine => ({
          id: machine?.id,
          machineName: machine?.machine_name,
          machineCode: machine?.machine_code,
          status: machine?.status,
          location: machine?.location
        })),
        machineCount: supplier?.machines?.length || 0,
        createdAt: supplier?.created_at,
        updatedAt: supplier?.updated_at
      })) || [];
    } catch (error) {
      console.error('Error fetching suppliers:', error);
      throw error;
    }
  },

  // Get supplier by ID with full details
  getSupplierById: async (id) => {
    try {
      const { data, error } = await supabase?.from('user_profiles')?.select(`
        *,
        case_assignments:supplier_case_assignments(
          id,
          case_id,
          machine_id,
          status,
          assigned_at,
          assigned_by,
          notes
        ),
        machines:machines(
          id,
          machine_name,
          machine_code,
          manufacturer,
          model,
          status,
          location,
          production_capacity,
          current_utilization
        )
      `)?.eq('id', id)?.single();
      
      if (error) throw error;
      
      return {
        id: data?.id,
        fullName: data?.full_name,
        email: data?.email,
        companyName: data?.company_name,
        phone: data?.phone,
        address: data?.address,
        caseAssignments: data?.case_assignments?.map(assignment => ({
          id: assignment?.id,
          caseId: assignment?.case_id,
          machineId: assignment?.machine_id,
          status: assignment?.status,
          assignedAt: assignment?.assigned_at,
          assignedBy: assignment?.assigned_by,
          notes: assignment?.notes
        })),
        machines: data?.machines?.map(machine => ({
          id: machine?.id,
          machineName: machine?.machine_name,
          machineCode: machine?.machine_code,
          manufacturer: machine?.manufacturer,
          model: machine?.model,
          status: machine?.status,
          location: machine?.location,
          productionCapacity: machine?.production_capacity,
          currentUtilization: machine?.current_utilization
        })),
        machineCount: data?.machines?.length || 0,
        totalCapacity: data?.machines?.reduce((sum, m) => sum + (m?.production_capacity || 0), 0),
        averageUtilization: data?.machines?.length > 0 
          ? data?.machines?.reduce((sum, m) => sum + (m?.current_utilization || 0), 0) / data?.machines?.length
          : 0,
        createdAt: data?.created_at,
        updatedAt: data?.updated_at
      };
    } catch (error) {
      console.error(`Error fetching supplier ${id}:`, error);
      throw error;
    }
  },

  // Get supplier's assigned cases (production batches)
  getSupplierCases: async (supplierId) => {
    try {
      // Get machines owned by this supplier
      const { data: machines, error: machinesError } = await supabase
        ?.from('machines')
        ?.select('id')
        ?.eq('supplier_id', supplierId);
      
      if (machinesError) throw machinesError;
      
      const machineIds = machines?.map(m => m?.id) || [];
      
      if (machineIds?.length === 0) {
        return [];
      }
      
      // Get production batches assigned to these machines
      const { data, error } = await supabase
        ?.from('production_batches')
        ?.select(`
          *,
          machine:machines(
            id,
            machine_name,
            machine_code
          )
        `)
        ?.in('machine_id', machineIds)
        ?.order('created_at', { ascending: false });
      
      if (error) throw error;
      
      return data?.map(batch => ({
        id: batch?.id,
        batchNumber: batch?.batch_number,
        patientName: batch?.patient_name,
        clinicName: batch?.clinic_name,
        status: batch?.status,
        priority: batch?.priority,
        progress: batch?.progress,
        startDate: batch?.start_date,
        deadline: batch?.deadline,
        completionDate: batch?.completion_date,
        machineId: batch?.machine_id,
        machine: batch?.machine ? {
          id: batch?.machine?.id,
          machineName: batch?.machine?.machine_name,
          machineCode: batch?.machine?.machine_code
        } : null,
        notes: batch?.notes,
        createdAt: batch?.created_at,
        updatedAt: batch?.updated_at
      })) || [];
    } catch (error) {
      console.error(`Error fetching supplier cases for ${supplierId}:`, error);
      throw error;
    }
  },

  // Get supplier's machines
  getSupplierMachines: async (supplierId) => {
    try {
      const { data, error } = await supabase
        ?.from('machines')
        ?.select('*')
        ?.eq('supplier_id', supplierId)
        ?.order('created_at', { ascending: false });
      
      if (error) throw error;
      
      return data?.map(machine => ({
        id: machine?.id,
        machineName: machine?.machine_name,
        machineCode: machine?.machine_code,
        manufacturer: machine?.manufacturer,
        model: machine?.model,
        status: machine?.status,
        location: machine?.location,
        installationDate: machine?.installation_date,
        lastMaintenanceDate: machine?.last_maintenance_date,
        nextMaintenanceDate: machine?.next_maintenance_date,
        operatingHours: machine?.operating_hours,
        productionCapacity: machine?.production_capacity,
        currentUtilization: machine?.current_utilization,
        notes: machine?.notes,
        supplierId: machine?.supplier_id,
        createdAt: machine?.created_at,
        updatedAt: machine?.updated_at
      })) || [];
    } catch (error) {
      console.error(`Error fetching supplier machines for ${supplierId}:`, error);
      throw error;
    }
  },

  // ============================================
  // TERRITORY & CAPACITY MANAGEMENT
  // ============================================

  // Get supplier network by territory
  getSupplierNetworkByTerritory: async () => {
    try {
      const suppliers = await supplierService?.getAllSuppliers();
      
      // Group by country
      const territoryMap = {};
      
      suppliers?.forEach(supplier => {
        supplier?.roleAssignments?.forEach(assignment => {
          if (assignment?.isActive && assignment?.countryId) {
            const countryKey = assignment?.countryId;
            
            if (!territoryMap?.[countryKey]) {
              territoryMap[countryKey] = {
                countryId: assignment?.countryId,
                countryName: assignment?.countryName,
                countryCode: assignment?.countryCode,
                suppliers: [],
                totalMachines: 0,
                totalCapacity: 0
              };
            }
            
            territoryMap?.[countryKey]?.suppliers?.push({
              id: supplier?.id,
              fullName: supplier?.fullName,
              companyName: supplier?.companyName,
              machineCount: supplier?.machineCount
            });
            
            territoryMap[countryKey].totalMachines += supplier?.machineCount || 0;
          }
        });
      });
      
      return Object.values(territoryMap);
    } catch (error) {
      console.error('Error fetching supplier network by territory:', error);
      throw error;
    }
  },

  // Get capacity analysis by market
  getCapacityAnalysisByMarket: async () => {
    try {
      const { data: countries, error: countriesError } = await supabase
        ?.from('countries')
        ?.select('id, country_code, country_name')
        ?.eq('is_active', true);
      
      if (countriesError) throw countriesError;
      
      const capacityData = [];
      
      for (const country of countries || []) {
        // Get suppliers for this country
        const suppliers = await supplierService?.getAllSuppliers({ countryId: country?.id });
        
        const totalMachines = suppliers?.reduce((sum, s) => sum + (s?.machineCount || 0), 0);
        const totalSuppliers = suppliers?.length;
        
        capacityData?.push({
          countryId: country?.id,
          countryCode: country?.country_code,
          countryName: country?.country_name,
          supplierCount: totalSuppliers,
          machineCount: totalMachines,
          averageMachinesPerSupplier: totalSuppliers > 0 ? (totalMachines / totalSuppliers)?.toFixed(1) : 0
        });
      }
      
      return capacityData;
    } catch (error) {
      console.error('Error fetching capacity analysis by market:', error);
      throw error;
    }
  },

  // Assign supplier to territory (country)
  assignSupplierToTerritory: async (userId, roleId, countryId, regionAccess = []) => {
    try {
      const { data, error } = await supabase?.from('user_role_assignments')?.insert({
        user_id: userId,
        role_id: roleId,
        country_id: countryId,
        region_access: regionAccess,
        is_active: true
      })?.select()?.single();
      
      if (error) throw error;
      
      return {
        id: data?.id,
        userId: data?.user_id,
        roleId: data?.role_id,
        countryId: data?.country_id,
        regionAccess: data?.region_access,
        isActive: data?.is_active,
        assignedAt: data?.assigned_at
      };
    } catch (error) {
      console.error('Error assigning supplier to territory:', error);
      throw error;
    }
  },

  // Update supplier territory assignment
  updateSupplierTerritory: async (assignmentId, updateData) => {
    try {
      const updates = {};
      if (updateData?.countryId !== undefined) updates.country_id = updateData?.countryId;
      if (updateData?.regionAccess !== undefined) updates.region_access = updateData?.regionAccess;
      if (updateData?.isActive !== undefined) updates.is_active = updateData?.isActive;
      
      const { data, error } = await supabase
        ?.from('user_role_assignments')
        ?.update(updates)
        ?.eq('id', assignmentId)
        ?.select()
        ?.single();
      
      if (error) throw error;
      
      return {
        id: data?.id,
        userId: data?.user_id,
        roleId: data?.role_id,
        countryId: data?.country_id,
        regionAccess: data?.region_access,
        isActive: data?.is_active
      };
    } catch (error) {
      console.error('Error updating supplier territory:', error);
      throw error;
    }
  },

  // Remove supplier from territory
  removeSupplierFromTerritory: async (assignmentId) => {
    try {
      const { error } = await supabase
        ?.from('user_role_assignments')
        ?.delete()
        ?.eq('id', assignmentId);
      
      if (error) throw error;
      
      return { success: true };
    } catch (error) {
      console.error('Error removing supplier from territory:', error);
      throw error;
    }
  }
};

export default supplierService;